import { Notifier } from './notifier';

// eslint-disable-next-line max-len
function isTouchEvent(e: MouseEvent | TouchEvent): e is TouchEvent {
  return e && 'touches' in e;
}

  type SliderConfig = {
    container: HTMLElement;
    toggler: HTMLElement;
    whiteLine: HTMLElement;
    min: number;
    max: number;
    initValue: number;
    input: HTMLInputElement;
  };

export class Slider extends Notifier {
  toggler: HTMLElement;

  container: HTMLElement;

  whiteLine: HTMLElement;

  input: HTMLInputElement;

  value: number;

  min: number;

  max: number;

  enable: boolean;

  constructor({
    container, toggler, whiteLine, input, min, max, initValue,
  }: SliderConfig) {
    super();
    this.toggler = toggler;
    this.container = container;
    this.whiteLine = whiteLine;
    this.input = input;
    this.min = min;
    this.max = max;
    this.toggler.addEventListener('mousedown', this.mouseDownHandler);
    this.toggler.addEventListener('touchstart', this.mouseDownHandler);
    this.input.addEventListener('input', (e) => {
      const { value } = e.target as HTMLInputElement;
      this.setValue(this.getValidValue(value));

      this.updateTogglePosition(this.getValidValue(value) / this.max);
    });
    this.updateTogglePosition(initValue / this.max);
    this.renderData(initValue / this.max);
    this.createValidator();
    this.value = initValue;
  }

  getValidValue = (value: string) => {
    const numberValue = Number(value);

    if (numberValue > this.max) {
      return this.max;
    }

    if (numberValue < this.min) {
      return this.min;
    }

    return numberValue;
  };

  createValidator = () => {
    this.input.addEventListener('input', (e) => {
      const { value } = e.target as HTMLInputElement;
      this.input.value = this.getValidValue(value).toString();
    });
  };

  mouseDownHandler = (e: MouseEvent | TouchEvent) => {
    const { left } = (e.target as HTMLElement).getBoundingClientRect();
    let pageX = 0;
    if (isTouchEvent(e)) {
      pageX = e.changedTouches[0].pageX;
    } else {
      pageX = e.pageX;
    }
    const offset = pageX - left;
    const handler = (mouseMoveEvent: MouseEvent | TouchEvent) => this.moveHandler(mouseMoveEvent, offset);
    window.addEventListener('mousemove', handler);
    window.addEventListener('touchmove', handler);
    this.enable = true;
    window.addEventListener('mouseup', () => {
      this.enable = false;

      window.removeEventListener('mousemove', handler);
    });
    window.addEventListener('touchend', () => {
      this.enable = false;
      window.removeEventListener('touchmove', handler);
    });
  };

  setValue = (value: number) => {
    this.value = value;
    this.notifySubsciptions();
  };

  renderData = (percent: number) => {
    const value = Math.floor(percent * this.max);
    this.input.value = value.toString();
    this.setValue(value);
  };

  updateTogglePosition = (percent: number) => {
    this.toggler.style.left = `${percent * 100}%`;
    this.toggler.style.transform = `translate(-${percent * 100}%, 0)`;
    this.whiteLine.style.width = `${100 - percent * 100}%`;
  };

  moveHandler = (e: MouseEvent | TouchEvent, offset: number) => {
    if (!this.enable) {
      return;
    }
    const { left: rootLeftBorder } = this.container.getBoundingClientRect();
    const togglerRect = this.toggler.getBoundingClientRect();
    let clientX = 0;
    if (isTouchEvent(e)) {
      clientX = e.touches[0].clientX;
    } else {
      clientX = e.clientX;
    }

    let togglerOffset = clientX - rootLeftBorder - offset;

    if (togglerOffset + 10 < 0) {
      togglerOffset = -10;
    }

    if (togglerOffset > this.container.clientWidth - togglerRect!.width + 10) {
      togglerOffset = this.container.clientWidth - togglerRect!.width + 10;
    }
    const percentForToggle = (togglerOffset) / (this.container.clientWidth - togglerRect!.width);
    const percentForValue = (togglerOffset + 10) / (this.container.clientWidth - togglerRect!.width + 20);

    window.requestAnimationFrame(() => this.updateTogglePosition(percentForToggle));
    this.renderData(percentForValue);
  };
}
